import { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar } from '../../../components/Calendar/Calendar';
import { Skeleton } from '../../../components/Skeleton';
import { Button } from '../../../components/shared/Button/Button';
import { HeadingUI3, HeadingUI4 } from '../../../components/shared/Heading';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { GuestRooms } from '../../../components/GuestRooms';
import { nanoid } from 'nanoid';
import { parseDate } from '../../../utils/dates';
import { RoomList } from '../../Rooms/RoomsList';
import { RoomDetailsContext } from '../../../Room/RoomDetails';
import { ParamsStore, RateMode } from '../../../stores/paramsStore';
import { OrderSummary } from '../../OrderSummary';
import { useSummaryParams } from '../../../utils/useSummaryParams';
import { errorStore } from '../../../stores/errorStore';
import { StickyBottom } from '../../../components/shared/StickyBottom/StickyBottom';
import { BackButton } from '../../../components/shared/BackButton/BackButton';
import { usePriceDiff } from '../../../utils/usePriceDiff';
import { NotFoundPlug } from '../../../components/Plug/NotFoundPlug';
import { format } from 'date-fns';
import { updateBooking } from '../../../apiMethods/updateBooking';
import { useRootStore } from '../../../stores/useRootStore';

export const EditBooking = observer(() => {
  const { configStore, paramsStore, bookingsStore, colorAvailabilityStore } = useRootStore();

  const { bookingId } = useParams<{ bookingId: string }>();

  const navigate = useNavigate();

  const hotelConfig = configStore.config;

  const loadingBooking = false;

  useEffect(() => {
    if (!bookingId) {
      console.warn('bookingId not found');
      return;
    }

    if (bookingsStore.bookingInfo && bookingsStore.bookingInfo.id == Number(bookingId)) {
      return;
    }

    bookingId && bookingsStore.loadBookingInfo(bookingId);
  }, [bookingsStore.bookingInfo]);

  /**---------- */
  useEffect(() => {
    if (!bookingsStore.bookingInfo) {
      return;
    }
    const { arrival, departure, rate_flow } = bookingsStore.bookingInfo;

    if (!arrival || !departure) {
      return;
    }

    const from = arrival ? parseDate(arrival) : undefined;
    const to = departure ? parseDate(departure) : undefined;
    const range = { from, to };

    const guestRooms = bookingsStore.bookingInfo.rooms_filter.map(roomParams => ({
      id: nanoid(),
      params: roomParams,
    }));

    paramsStore.setRange(range);
    paramsStore.setGuestRooms(guestRooms);
    paramsStore.setRateMode(rate_flow as RateMode);
    colorAvailabilityStore.init();
    return () => {
      bookingsStore.setBookingInfo(null);
    };
  }, [bookingsStore.bookingInfo]);

  /**---------- */
  const [step, setStep] = useState(1);

  const goStepBack = () => {
    setStep(step => step - 1);
  };

  const searchRooms = async () => {
    setStep(step => step + 1);
    await paramsStore.loadRooms(Number(bookingId));
  };

  const basePrice = useMemo(() => {
    if (!bookingsStore.bookingInfo) {
      return 0;
    }

    return Number(bookingsStore.bookingInfo.price);
  }, [bookingsStore.bookingInfo]);

  const { priceDiff: priceChange, sign: priceChangeSign } = usePriceDiff(
    paramsStore.room?.min_price ?? 0,
    basePrice,
  );

  if (bookingsStore.bookingInfo?.read_only) {
    return (
      <div className="container mx-auto mt-10 max-w-[870px]">
        <NotFoundPlug title="Страница не найдена" text={'Изменение заказа недоступно'}>
          <Button onClick={() => navigate(-1)}>Вернуться к бронированию</Button>
        </NotFoundPlug>
      </div>
    );
  }

  if (!bookingId) {
    return (
      <div className="container mx-auto mt-10 max-w-[870px]">
        <NotFoundPlug title="Номер бронирования не найден" text="">
          <Button onClick={() => navigate(-1)}>Вернуться к бронированию</Button>
        </NotFoundPlug>
      </div>
    );
  }

  return (
    <main className="container mx-auto max-w-[1132px] pt-5">
      {step > 1 ? (
        <BackButton className="!p-5" onClick={goStepBack}>
          К параметрам
        </BackButton>
      ) : (
        <BackButton className="!p-5" onClick={() => navigate(-1)}>
          К бронированию
        </BackButton>
      )}
      {step == 1 && (
        <>
          <HeadingUI3 className="mb-7 px-5 3xl:mt-3">
            <Skeleton loading={loadingBooking} className="h-9 w-[260px]">
              Даты и количество номеров
              {/* Изменение бронирования №{bookingsStore.bookingInfo?.crs_number} */}
            </Skeleton>
          </HeadingUI3>
          {paramsStore.range ? (
            <>
              <Calendar
                range={paramsStore.range}
                onChange={newRange => paramsStore.setRange(newRange)}
              />
            </>
          ) : null}
          {hotelConfig && (
            <section className="mb-10 mt-10 flex flex-col justify-center xl:flex-row">
              <GuestRooms
                guestRooms={paramsStore.guestRooms}
                onChange={paramsStore.setGuestRooms}
                childCategories={hotelConfig.hotel_child_categories}
                maxAdults={hotelConfig.max_adults}
                maxRooms={hotelConfig.max_rooms}
              />
            </section>
          )}
        </>
      )}
      {step == 2 && (
        <>
          <HeadingUI3 className="mb-7 px-5 3xl:mt-3">
            <Skeleton className="h-9 w-[260px]">Выбор номера</Skeleton>
          </HeadingUI3>
          <RoomsStage
            paramsStore={paramsStore}
            selectedRoomId={bookingsStore.bookingInfo?.room_type.id}
            nextStage={() => setStep(step => step + 1)}
            prevStage={() => setStep(step => step - 1)}
            rateId={bookingsStore.bookingInfo?.rate?.id}
            basePrice={basePrice}
          />
        </>
      )}
      {step == 3 && (
        <ConfirmationStage
          paramsStore={paramsStore}
          priceChange={priceChange}
          priceChangeSign={priceChangeSign}
          bookingId={bookingId}
        />
      )}

      {step == 1 && (
        <StickyBottom>
          <Button onClick={searchRooms} className="w-full xl:w-auto">
            Подобрать номера
          </Button>
        </StickyBottom>
      )}
    </main>
  );
});

const RoomsStage = ({
  nextStage,
  prevStage,
  paramsStore,
  selectedRoomId,
  rateId,
  basePrice,
}: {
  paramsStore: ParamsStore;
  nextStage: () => void;
  prevStage: () => void;
  selectedRoomId?: number;
  rateId?: number;
  basePrice?: number;
}) => {
  const navigate = useNavigate();

  const closeRoomDetails = () => {
    navigate(-1);
  };

  const onRoomSelect = useCallback(
    (roomId?: number) => {
      roomId && paramsStore?.selectRoom(roomId);

      try {
        paramsStore.assertRateExists(rateId);
      } catch (err) {
        return errorStore.setError(err as Error);
      }

      // update rate in the store
      paramsStore.selectRate(rateId ?? null);
      nextStage();
    },
    [rateId],
  );

  const getRoom = (roomId: number) => paramsStore.getRoom(roomId);

  return (
    <RoomDetailsContext.Provider
      value={{ getRoom, rateMode: paramsStore.rateMode, onRoomSelect, closeRoomDetails, basePrice }}
    >
      <RoomList
        selectedRoomId={selectedRoomId}
        roomList={paramsStore.roomList}
        nightsCount={paramsStore.nightsCount}
        isDisabled={paramsStore.hasUnappliedParams}
        onRoomSelect={onRoomSelect}
        onGoBack={prevStage}
      />
    </RoomDetailsContext.Provider>
  );
};

const PaymentRedirectText = 'Вы будете перенаправлены на страницу оплаты';
const BookingInfoRedirectText = 'Вы будете перенправлены на страницу бронирования';

const ConfirmationStage = ({
  paramsStore,
  priceChange,
  priceChangeSign,
  bookingId,
}: {
  paramsStore: ParamsStore;
  bookingId: string;
  priceChange: number | null;
  priceChangeSign: string;
}) => {
  const params = useSummaryParams(paramsStore);

  const [updatePending, setUpdatePending] = useState(false);

  const [redirectCountdown, setRedirectCountdown] = useState<null | number>(null);
  const [redirectText, setRedirectText] = useState<null | string>(null);

  const navigate = useNavigate();

  const submitReservationChanges = async () => {
    const { range, guestRooms } = paramsStore.searchParams || {};
    const roomTypeId = paramsStore.room?.id;

    if (!roomTypeId || !range?.from || !range.to) {
      return;
    }

    const arrival = format(range.from, 'yyyy-MM-dd');
    const departure = format(range.to, 'yyyy-MM-dd');

    const body = {
      reservation_id: Number(bookingId),
      rooms: guestRooms.flatMap(x => x.params),
      arrival,
      departure,
      room_type_id: roomTypeId,
    };

    setUpdatePending(true);
    try {
      const { payment_link } = (await updateBooking(body)) ?? {};

      const countdown = 3;
      setRedirectCountdown(countdown);
      setRedirectText(payment_link ? PaymentRedirectText : BookingInfoRedirectText);

      setTimeout(() => {
        if (!payment_link) {
          navigate(-1);
        } else {
          window.location.assign(payment_link);
        }
      }, countdown * 1000);

      const intervalId = setInterval(
        () =>
          setRedirectCountdown(count => {
            if (!count || count == 0) {
              clearInterval(intervalId);
              return 1;
            }
            return count - 1;
          }),
        1000,
      );
    } finally {
      setUpdatePending(false);
    }
  };

  return (
    <div>
      <HeadingUI3 className="mb-7 px-5 3xl:mt-3">
        <Skeleton className="h-9 w-[260px]">Подтверждение параметров</Skeleton>
      </HeadingUI3>
      <main className="container mx-auto w-fit max-w-[768px]">
        <div className="mt-10 flex ">
          {redirectCountdown != null ? (
            <div className="flex flex-col">
              <HeadingUI4>Изменения сохранены</HeadingUI4>
              <span>
                {redirectText} {`через ${redirectCountdown} c`}
              </span>
            </div>
          ) : (
            <section className="sticky top-8 block h-fit bg-neutral-10 p-8">
              <OrderSummary
                serviceStore={paramsStore.serviceStore}
                params={{ ...params, priceChange, priceChangeSign }}
                showServices
              />
            </section>
          )}
        </div>
        {redirectCountdown == null && (
          <div className="p-5 xl:p-0 xl:py-8">
            <Button
              className="w-full xl:w-auto"
              onClick={submitReservationChanges}
              pending={updatePending}
            >
              Сохранить изменения
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};
